import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import generalApi from "../api/generalApi";

const Faqs = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadFaqs = async () => {
      try {
        const data = await generalApi.getFaqs();
        setFaqs(data.length > 0 ? data : []);
      } catch {
        setFaqs([]);
      } finally {
        setLoading(false);
      }
    };
    loadFaqs();
  }, []);

  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", color: "#EE2E24" }}>Loading FAQs...</div>
      </div>
    );
  }

  return (
    <div
      style={{
        background: "linear-gradient(135deg, #F5F5F5 0%, #FFFFFF 100%)",
        minHeight: "100vh",
        padding: 0,
        margin: 0,
        fontFamily: "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      }}
    >
      {/* Enhanced Header with Animation */}
      <div
        style={{
          background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
          color: "#fff",
          padding: "60px 20px 40px 20px",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative Circles */}
        <div
          style={{
            position: "absolute",
            top: "-50px",
            right: "-50px",
            width: "200px",
            height: "200px",
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.1)",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-30px",
            left: "-30px",
            width: "150px",
            height: "150px",
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.1)",
          }}
        />

        <div style={{ position: "relative", zIndex: 1 }}>
          <h1
            style={{
              fontSize: "2.8rem",
              fontWeight: "800",
              marginBottom: 12,
              paddingTop: "15px",
              letterSpacing: "-1px",
              textShadow: "0 2px 10px rgba(0,0,0,0.1)",
            }}
          >
            Frequently Asked Questions
          </h1>
          <p
            style={{
              fontSize: "1.1rem",
              opacity: 0.95,
              fontWeight: "500",
              maxWidth: "600px",
              margin: "0 auto",
            }}
          >
            Find answers to common queries about Trivana Hospitality and our
            services.
          </p>
        </div>
      </div>

      <div
        style={{
          maxWidth: 800,
          margin: "50px auto",
          padding: "0 20px 50px 20px",
        }}
      >
        {faqs.map((faq, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1, duration: 0.4 }}
            style={{
              borderRadius: 16,
              background: "#fff",
              boxShadow: "0 4px 15px rgba(238, 46, 36, 0.08)",
              marginBottom: 20,
              border: "1px solid rgba(238, 46, 36, 0.1)",
              overflow: "hidden",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-4px)";
              e.currentTarget.style.boxShadow =
                "0 8px 25px rgba(238, 46, 36, 0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                "0 4px 15px rgba(238, 46, 36, 0.08)";
            }}
          >
            <button
              onClick={() => toggleFAQ(index)}
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "20px 24px",
                background:
                  openIndex === index
                    ? "linear-gradient(135deg, rgba(238, 46, 36, 0.05) 0%, rgba(255, 196, 0, 0.05) 100%)"
                    : "none",
                border: "none",
                outline: "none",
                fontSize: "1.05rem",
                fontWeight: 700,
                color: "#2E2E2E",
                cursor: "pointer",
                transition: "all 0.3s ease",
                letterSpacing: "-0.3px",
              }}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  flex: 1,
                  textAlign: "left",
                }}
              >
                <span
                  style={{
                    background:
                      "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                    color: "#FFFFFF",
                    padding: "6px 14px",
                    borderRadius: 8,
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    minWidth: "45px",
                    textAlign: "center",
                    boxShadow: "0 2px 8px rgba(238, 46, 36, 0.25)",
                  }}
                >
                  Q{index + 1}
                </span>
                <span>{faq.question}</span>
              </span>
              <span
                style={{
                  color: "#EE2E24",
                  transition: "transform 0.3s ease",
                  transform:
                    openIndex === index ? "rotate(180deg)" : "rotate(0deg)",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {openIndex === index ? <Minus size={22} /> : <Plus size={22} />}
              </span>
            </button>

            <AnimatePresence>
              {openIndex === index && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  style={{
                    overflow: "hidden",
                    background:
                      "linear-gradient(135deg, #F9F9F9 0%, #F5F5F5 100%)",
                    color: "#2E2E2E",
                    borderTop: "1px solid rgba(238, 46, 36, 0.1)",
                  }}
                >
                  <p
                    style={{
                      padding: "20px 24px 20px 83px",
                      fontSize: "0.95rem",
                      margin: 0,
                      lineHeight: "1.7",
                      opacity: 0.9,
                    }}
                  >
                    {faq.answer}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}

        {/* Enhanced Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: faqs.length * 0.1 + 0.2 }}
          style={{
            marginTop: 40,
            textAlign: "center",
            padding: "30px",
            background:
              "linear-gradient(135deg, rgba(238, 46, 36, 0.05) 0%, rgba(255, 196, 0, 0.05) 100%)",
            borderRadius: 16,
            border: "1px solid rgba(238, 46, 36, 0.1)",
          }}
        >
          <p
            style={{
              color: "#2E2E2E",
              fontSize: "1rem",
              marginBottom: 16,
              fontWeight: 600,
            }}
          >
            Can't find the answer you're looking for?
          </p>
          <a
            href="/contact"
            style={{
              display: "inline-block",
              background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
              color: "#FFFFFF",
              fontWeight: 700,
              textDecoration: "none",
              padding: "12px 32px",
              borderRadius: 8,
              fontSize: "0.95rem",
              boxShadow: "0 4px 15px rgba(238, 46, 36, 0.3)",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 6px 20px rgba(238, 46, 36, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 15px rgba(238, 46, 36, 0.3)";
            }}
          >
            Contact our support team
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default Faqs;
